import "@/App.css";
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { track } from "@/lib/analytics";

// Home is the LCP route — keep eager
import Home from "@/pages/Home";
import WhatsAppFab from "@/components/WhatsAppFab";
import PWAInstallBanner from "@/components/PWAInstallBanner";

// Lazy-load all other routes for smaller initial JS bundle
const FerryResults = lazy(() => import("@/pages/FerryResults"));
const FerryRouteLanding = lazy(() => import("@/pages/FerryRouteLanding"));
const BookingFlow = lazy(() => import("@/pages/BookingFlow"));
const HotelsPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.HotelsPage })));
const ToursPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.ToursPage })));
const CarsPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.CarsPage })));
const VisaPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.VisaPage })));
const DestinationPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.DestinationPage })));
const CampaignsPage = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.CampaignsPage })));
const LoginPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.RegisterPage })));
const AuthCallback = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.AuthCallback })));
const Profile = lazy(() => import("@/pages/Profile"));
const Admin = lazy(() => import("@/pages/Admin"));
const Contact = lazy(() => import("@/pages/Contact"));
const Verify = lazy(() => import("@/pages/Verify"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DistanceSalesAgreement = lazy(() => import("@/pages/DistanceSalesAgreement"));
const ReservationLookup = lazy(() => import("@/pages/ReservationLookup"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const TourDetail = lazy(() => import("@/pages/TourDetail"));
const HotelDetail = lazy(() => import("@/pages/HotelDetail"));
const CarDetail = lazy(() => import("@/pages/CarDetail"));
const VisaDetail = lazy(() => import("@/pages/VisaDetail"));

// 301-style SEO-safe redirect for the legacy /arac/:id path → /arackiralama/:id
function RedirectCar() {
  const { id } = useParams();
  return <Navigate to={`/arackiralama/${id}`} replace />;
}

// Minimal suspense fallback — keeps layout stable, no CLS
function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} aria-hidden="true" />;
}

function AppRouter() {
  const location = useLocation();
  // GA4 + Google Ads SPA page_view tracking (initial load fires via gtag config)
  const isFirstView = useRef(true);
  useEffect(() => {
    if (isFirstView.current) { isFirstView.current = false; return; }
    track("page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  // Handle Emergent Google Auth callback first (hash-based session_id)
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  const hideFab = location.pathname.startsWith("/admin") || location.pathname.startsWith("/dogrula");
  return (
    <>
      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/feribot" element={<Home />} />
            <Route path="/feribot/arama" element={<FerryResults />} />
            <Route path="/feribot/hat/:routeSlug" element={<FerryRouteLanding />} />
            <Route path="/rezervasyon/:module/:item_id" element={<BookingFlow />} />
            <Route path="/otel" element={<HotelsPage />} />
            <Route path="/otel/:id" element={<HotelDetail />} />
            <Route path="/tur" element={<ToursPage />} />
            <Route path="/tur/:id" element={<TourDetail />} />
            <Route path="/arackiralama" element={<CarsPage />} />
            <Route path="/arackiralama/:id" element={<CarDetail />} />
            {/* Backwards-compat redirects for old /arac path */}
            <Route path="/arac" element={<Navigate to="/arackiralama" replace />} />
            <Route path="/arac/:id" element={<RedirectCar />} />
            <Route path="/vize" element={<VisaPage />} />
            <Route path="/vize/:id" element={<VisaDetail />} />
            <Route path="/destinasyon/:slug" element={<DestinationPage />} />
            <Route path="/kampanyalar" element={<CampaignsPage />} />
            <Route path="/giris" element={<LoginPage />} />
            <Route path="/kayit" element={<RegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/profil" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/iletisim" element={<Contact />} />
            <Route path="/rezervasyon-sorgula" element={<ReservationLookup />} />
            <Route path="/odeme-sonuc" element={<PaymentResult />} />
            <Route path="/gizlilik" element={<Privacy />} />
            <Route path="/kosullar" element={<Terms />} />
            <Route path="/mesafeli-satis" element={<DistanceSalesAgreement />} />
            <Route path="/dogrula/:pnr" element={<Verify />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      </main>
      {!hideFab && <WhatsAppFab />}
      {!hideFab && <PWAInstallBanner />}
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
